import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp, DollarSign, Users, Sparkles, Eye, MousePointer,
  ChevronUp, ChevronDown, ChevronRight, Check, Menu, Share2
} from 'lucide-react';
import {
  Box, Typography, Alert, Menu as MuiMenu,
  MenuItem, Chip, Divider, IconButton, CircularProgress,
  Button, useMediaQuery, useTheme
} from '@mui/material';
import ModernShareButton from '../ShareCircleButton/ModernShareButton';
import { useCircleData } from './CircleDataContext';

// Import circle service for any direct API operations
import { circleService } from '../../services/circleService';

/**
 * NextGenSavingsLadder - A responsive savings ladder component.
 * This component automatically adapts to different screen sizes and provides
 * an optimal viewing experience across devices (mobile, tablet, desktop).
 */
const NextGenSavingsLadder = ({
  darkMode = false,
  initialCircleId = null, // Still keep for backward compatibility
  handleShareCircle,
  getAuthToken = null, // Add getAuthToken prop for any direct API needs
  forceIsMobile = null // Optional prop to force mobile view regardless of screen size
}) => {
  // Get the theme to use for responsive behavior
  const theme = useTheme();

  // Determine if we're on a mobile device based on screen size
  // Can be overridden with forceIsMobile prop
  const isSystemMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = forceIsMobile !== null ? forceIsMobile : isSystemMobile;

  // Refs
  const scrollContainerRef = useRef(null);

  // Get shared circle data from context
  const {
    userCircles,
    tierData,
    tiers,
    isLoading,
    error,
    selectedCircleId,
    setSelectedCircleId
  } = useCircleData();

  // State for animations and interactions
  const [activeHover, setActiveHover] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [animateNextTier, setAnimateNextTier] = useState(false);
  const [rotationValues, setRotationValues] = useState({ x: 0, y: 0 });
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(true);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [shareError, setShareError] = useState(null);

  // Set initial circle from prop if provided
  useEffect(() => {
    if (initialCircleId && selectedCircleId !== initialCircleId) {
      setSelectedCircleId(initialCircleId);
    }
  }, [initialCircleId, selectedCircleId, setSelectedCircleId]);

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);

  // Share metrics state (this stays local to the component)
  const [shareMetrics, setShareMetrics] = useState({
    views: 155,
    clicks: 74,
    recentActivity: true
  });

  // Handle Circle Selection Menu
  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSelectCircle = (circleId) => {
    setSelectedCircleId(circleId);
    handleCloseMenu();
  };

  // Enhanced share handler that uses the circleService
  const handleShare = async (circleId, shareData) => {
    // Clear any previous errors
    setShareError(null);
    
    try {
      // If we have direct auth token access, use the service to share
      if (getAuthToken && circleId) {
        const token = await getAuthToken();
        await circleService.shareCircle(circleId, shareData, token);
      }
      
      // Call the parent handler as well (for UI feedback)
      if (handleShareCircle) {
        handleShareCircle(circleId, shareData);
      }
      
      // Update metrics to simulate sharing activity
      setShareMetrics(prev => ({
        ...prev,
        views: prev.views + 1,
        recentActivity: true
      }));
      
      // Reset recent activity flag after animation
      setTimeout(() => {
        setShareMetrics(prev => ({
          ...prev,
          recentActivity: false
        }));
      }, 3000);
      
    } catch (error) {
      console.error('Error sharing circle:', error);
      setShareError(error.friendlyMessage || 'Failed to share circle. Please try again.');
    }
  };

  // Mouse movement effect for 3D card rotation
  const handleMouseMove = (e) => {
    const card = e.currentTarget.getBoundingClientRect();
    const centerX = card.left + card.width / 2;
    const centerY = card.top + card.height / 2;
    const posX = e.clientX - centerX;
    const posY = e.clientY - centerY;

    // Calculate rotation values based on mouse position
    const rotateX = (posY / card.height) * -10; // Invert for natural feel
    const rotateY = (posX / card.width) * 10;

    setRotationValues({ x: rotateX, y: rotateY });

    // Update general mouse position for other effects
    setMousePosition({
      x: (e.clientX - card.left) / card.width,
      y: (e.clientY - card.top) / card.height
    });
  };

  // Reset rotation when mouse leaves
  const handleMouseLeave = () => {
    setRotationValues({ x: 0, y: 0 });
  };

  // Handle scroll events to update shadows
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    setScrollPosition(scrollTop);
    setShowTopShadow(scrollTop > 10);
    setShowBottomShadow(scrollTop < scrollHeight - clientHeight - 10);
  };

  // Scroll to a specific tier
  const scrollToTier = (index) => {
    if (scrollContainerRef.current) {
      // Calculate position (each tier card is ~90px tall)
      // For mobile, cards are slightly shorter
      const cardHeight = isMobile ? 80 : 90;
      const targetPosition = Math.max(0, index * cardHeight);

      // Smoothly scroll to the target position
      scrollContainerRef.current.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Scroll up one tier
  const scrollUp = () => {
    if (scrollContainerRef.current) {
      const cardHeight = isMobile ? 80 : 90;
      const currentVisibleTier = Math.floor(scrollPosition / cardHeight);
      if (currentVisibleTier > 0) {
        scrollToTier(currentVisibleTier - 1);
      }
    }
  };

  // Scroll down one tier
  const scrollDown = () => {
    if (scrollContainerRef.current) {
      const cardHeight = isMobile ? 80 : 90;
      const currentVisibleTier = Math.floor(scrollPosition / cardHeight);
      if (currentVisibleTier < tiers.length - 1) {
        scrollToTier(currentVisibleTier + 1);
      }
    }
  };

  // Animate Next Tier periodically
  useEffect(() => {
    const animationInterval = setInterval(() => {
      setAnimateNextTier(true);

      // Reset animation after duration
      setTimeout(() => {
        setAnimateNextTier(false);
      }, 3000);
    }, 7000);

    return () => {
      clearInterval(animationInterval);
    };
  }, []);

  // Simulate increasing views/clicks over time
  useEffect(() => {
    const interval = setInterval(() => {
      if (Math.random() > 0.7) {
        setShareMetrics(prev => ({
          ...prev,
          views: prev.views + 1,
          recentActivity: true
        }));

        // 50% chance that a view also results in a click
        if (Math.random() > 0.5) {
          setTimeout(() => {
            setShareMetrics(prev => ({
              ...prev,
              clicks: prev.clicks + 1
            }));
          }, 1500);
        }

        // Reset "recent" flag after animation
        setTimeout(() => {
          setShareMetrics(prev => ({
            ...prev,
            recentActivity: false
          }));
        }, 3000);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Scroll to current/next tier on initial load or when tiers change
  useEffect(() => {
    if (scrollContainerRef.current && tiers.length > 0) {
      // Find indices of important tiers
      const currentTierIndex = tiers.findIndex(tier => tier.isCurrent);
      const nextTierIndex = tiers.findIndex(tier => tier.isNext);

      // Scroll to the next tier if present, otherwise to the current tier
      const targetIndex = nextTierIndex >= 0 ? nextTierIndex : currentTierIndex >= 0 ? currentTierIndex : 0;

      // Add a small delay to ensure the component is fully rendered
      setTimeout(() => {
        scrollToTier(targetIndex);
      }, 300);
    }
  }, [tiers, isMobile]);

  // Get current and next tier from tiers array
  const currentTier = tiers.find(tier => tier.isCurrent);
  const nextTier = tiers.find(tier => tier.isNext);

  // Calculate savings difference between current and next tier
  const savingsPerMember = currentTier && nextTier ? currentTier.price - nextTier.price : 0;

  // Check if user has reached maximum tier
  const isMaxTier = !nextTier || (nextTier.price === currentTier?.price);

  // Format helpers
  const formatCurrency = (value) => `$${value.toFixed(2)}`;
  const formatSavings = (value) => `${value.toFixed(2)}%`;

  // CSS-in-JS translation of the glass effect and keyframes
  const glassContainerStyle = {
    padding: isMobile ? '1.5rem 1rem' : '1.5rem',
    background: isMobile ? 'transparent' : 'var(--background-paper)',
    backdropFilter: isMobile ? 'none' : 'blur(10px)',
    WebkitBackdropFilter: isMobile ? 'none' : 'blur(10px)',
    borderRadius: isMobile ? '0' : '16px',
    border: isMobile ? 'none' : `1px solid var(--border-color)`,
    boxShadow: isMobile ? 'none' : `0 10px 40px ${darkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.08)'}`,
    maxWidth: isMobile ? '100%' : 600,
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
    margin: isMobile ? '0' : 'auto',
  };

  // Get the currently selected circle
  const selectedCircle = userCircles.find(circle => circle.id === selectedCircleId);

  // Show loading state
  if (isLoading) {
    return (
      <Box sx={glassContainerStyle} className="savings-ladder-container">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: isMobile ? 200 : 300 }}>
          <CircularProgress size={isMobile ? 32 : 40} sx={{ color: 'var(--primary)' }} />
          <Typography sx={{ ml: 2, fontSize: isMobile ? '0.9rem' : '1rem' }}>Loading pricing tiers...</Typography>
        </Box>
      </Box>
    );
  }

  // Show error state when there's an error and no tier data
  if (error && (!tierData || tiers.length === 0)) {
    return (
      <Box sx={glassContainerStyle} className="savings-ladder-container">
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Typography sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>
          Unable to load pricing data. Please try again later.
        </Typography>
      </Box>
    );
  }

  // Return empty state if we have no tiers to display
  if (tiers.length === 0) {
    return (
      <Box sx={glassContainerStyle} className="savings-ladder-container">
        <Alert severity="info">No pricing tiers available at this time.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={glassContainerStyle}
      className="savings-ladder-container"
    >
      {/* Header with Plan Selector */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: isMobile ? 1.5 : 2,
        pb: isMobile ? 1 : null,
        position: 'relative'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TrendingUp
            size={isMobile ? 16 : 18}
            style={{
              color: darkMode ? 'var(--primary-light)' : 'rgb(79, 70, 229)',
              marginRight: '8px'
            }}
          />
          <Typography
            variant={isMobile ? "subtitle1" : "h6"}
            sx={{
              fontWeight: 700,
              color: 'var(--text-primary)',
              fontSize: isMobile ? '1.1rem' : '1.25rem'
            }}
            className="section-title"
          >
            Complete Savings Ladder
          </Typography>
        </Box>

        {/* Plan Selector Button - Only show if user has multiple circles */}
        {userCircles.length > 1 && (
          <Box>
            <IconButton
              onClick={handleOpenMenu}
              sx={{
                border: '1px solid',
                borderColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
                p: 0.75,
                width: isMobile ? 32 : 36,
                height: isMobile ? 32 : 36
              }}
            >
              <Menu size={isMobile ? 14 : 16} color={darkMode ? '#f1f5f9' : '#0f172a'} />
            </IconButton>

            <MuiMenu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
              PaperProps={{
                sx: {
                  mt: 1,
                  width: isMobile ? 240 : 280,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                  borderRadius: '12px',
                  bgcolor: darkMode ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(8px)'
                }
              }}
            >
              <Typography
                sx={{
                  px: 2,
                  py: 1.5,
                  fontWeight: 600,
                  color: darkMode ? '#e2e8f0' : '#334155',
                  fontSize: isMobile ? '0.875rem' : '0.9375rem'
                }}
              >
                Select Circle
              </Typography>
              <Divider sx={{ mb: 1 }} />

              {userCircles.map((circle) => (
                <MenuItem
                  key={circle.id}
                  onClick={() => handleSelectCircle(circle.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    py: 1.5,
                    px: 2,
                    position: 'relative',
                    '&:hover': {
                      bgcolor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'
                    }
                  }}
                >
                  <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32 }}>
                    {selectedCircleId === circle.id && (
                      <Check
                        size={16}
                        color={darkMode ? 'var(--primary-light)' : 'rgb(79, 70, 229)'}
                      />
                    )}
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: darkMode ? '#e2e8f0' : '#334155',
                        fontSize: isMobile ? '0.875rem' : '0.9375rem'
                      }}
                    >
                      {circle.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: darkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                        fontSize: isMobile ? '0.75rem' : '0.8125rem'
                      }}
                    >
                      {circle.plan || 'Circle Plan'}
                    </Typography>
                  </Box>

                  {/* Status chips */}
                  <Box sx={{ ml: 'auto' }}>
                    {circle.total_joiners && (
                      <Chip
                        label={`${circle.total_joiners} member${circle.total_joiners !== 1 ? 's' : ''}`}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.625rem',
                          bgcolor: darkMode ? 'rgba(79, 70, 229, 0.2)' : 'rgba(79, 70, 229, 0.1)',
                          color: darkMode ? 'var(--primary-light)' : 'rgb(79, 70, 229)',
                          borderRadius: '4px'
                        }}
                      />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </MuiMenu>
          </Box>
        )}

        {/* Red underline accent for mobile design */}
        {isMobile && (
          <Box sx={{
            position: 'absolute',
            left: '6%',
            bottom: 0,
            width: '20%',
            height: 3,
            backgroundColor: '#ff4775',
            borderRadius: '3px 3px 0 0'
          }} />
        )}
      </Box>

      {/* Circle/Plan Info Bar */}
      {tierData && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: isMobile ? 1.5 : 2.5,
            pb: isMobile ? 1 : 1.5,
            borderBottom: '1px solid',
            borderColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
          }}
        >
          <Box>
            <Typography
              sx={{
                fontWeight: 600,
                color: 'var(--text-primary)',
                fontSize: isMobile ? '0.875rem' : '0.9375rem'
              }}
            >
              {tierData.circleName}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: darkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                alignItems: 'center',
                fontSize: isMobile ? '0.75rem' : '0.8125rem'
              }}
            >
              {tierData.carrierDisplay} <ChevronRight size={12} style={{ opacity: 0.6, margin: '0 2px' }} /> {tierData.carrierPlan}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Chip
              label={`${tierData.totalJoiners} member${tierData.totalJoiners !== 1 ? 's' : ''}`}
              size="small"
              sx={{
                height: isMobile ? 22 : 24,
                fontSize: isMobile ? '0.7rem' : '0.75rem',
                bgcolor: darkMode ? 'rgba(79, 70, 229, 0.2)' : 'rgba(79, 70, 229, 0.1)',
                color: darkMode ? 'var(--primary-light)' : 'rgb(79, 70, 229)',
                fontWeight: 600,
                borderRadius: isMobile ? '10px' : '12px'
              }}
            />
          </Box>
        </Box>
      )}

      {/* Error alert - shown but still displaying the data */}
      {(error || shareError) && (
        <Alert severity="warning" sx={{ mb: 2, fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
          {shareError || error}
        </Alert>
      )}

      {/* Top shadow indicator when scrolled */}
      {showTopShadow && (
        <Box
          sx={{
            position: 'absolute',
            top: isMobile ? (tiers.length > 0 ? 100 : 70) : (tiers.length > 0 ? 130 : 80), // Below the header and plan info
            left: 0,
            right: 0,
            height: 8,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.05), transparent)',
            zIndex: 2,
            pointerEvents: 'none'
          }}
        />
      )}

      {/* Scroll up button */}
      {showTopShadow && (
        <Box
          sx={{
            position: 'absolute',
            top: isMobile ? (tiers.length > 0 ? 90 : 60) : (tiers.length > 0 ? 120 : 70),
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 3
          }}
        >
          <motion.div
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <Box
              onClick={scrollUp}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: isMobile ? 24 : 26,
                height: isMobile ? 24 : 26,
                borderRadius: '50%',
                bgcolor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                cursor: 'pointer',
                border: '1px solid',
                borderColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.05)'
              }}
            >
              <ChevronUp size={isMobile ? 14 : 16} color={darkMode ? 'rgba(255, 255, 255, 0.8)' : '#6B7280'} />
            </Box>
          </motion.div>
        </Box>
      )}

      {/* Tiers - Main Scrollable Container */}
      <Box
        sx={{
          position: 'relative',
          mb: 2,
          maxHeight: isMobile ? 350 : 400,
          overflow: 'auto',
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE/Edge
          '&::-webkit-scrollbar': { // Hide scrollbar for Chrome/Safari/Opera
            display: 'none'
          }
        }}
        ref={scrollContainerRef}
        onScroll={handleScroll}
      >
        {/* Curved connector line between tiers */}
        <Box
          component={motion.div}
          sx={{
            position: 'absolute',
            zIndex: 0,
            left: isMobile ? 30 : 88,
            top: 0,
            bottom: 0,
            width: isMobile ? 4 : 6,
            background: darkMode
              ? 'linear-gradient(to bottom, rgba(99, 102, 241, 0.7), rgba(59, 130, 246, 0.8), rgba(56, 189, 248, 0.9))'
              : 'linear-gradient(to bottom, rgba(79, 70, 229, 0.8), rgba(59, 130, 246, 0.9), rgba(14, 165, 233, 1))',
            borderRadius: '2px',
            boxShadow: '0 0 8px rgba(56, 189, 248, 0.3)',
            height: tiers.length * (isMobile ? 90 : 100)
          }}
          initial={{ opacity: 0, scaleY: 0 }}
          animate={{ opacity: 1, scaleY: 1 }}
          transition={{ duration: 1, ease: "easeOut" }}
        />

        {/* Map through all tiers */}
        {tiers.map((tier) => (
          <Box
            component={motion.div}
            key={tier.members}
            className={`tier-card ${tier.isCurrent ? 'current' : ''} ${tier.isNext ? 'next' : ''}`}
            sx={{
              position: 'relative',
              borderRadius: isMobile ? '12px' : '16px',
              p: isMobile ? 1.5 : 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: tier.isCurrent
                ? (darkMode ? 'var(--tier-card-current)' : 'rgba(238, 242, 255, 1)')
                : tier.isNext
                  ? (darkMode ? 'var(--tier-card-next)' : 'rgba(224, 242, 254, 0.8)')
                  : (darkMode ? 'var(--tier-card-bg)' : 'white'),
              border: tier.isCurrent
                ? `2px solid ${darkMode ? 'rgba(99, 102, 241, 0.3)' : 'rgba(99, 102, 241, 0.2)'}`
                : tier.isNext
                  ? `2px solid ${darkMode ? 'rgba(56, 189, 248, 0.3)' : 'rgba(56, 189, 248, 0.2)'}`
                  : `1px solid var(--border-color)`,
              mb: 1.5
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{
              opacity: 1,
              y: 0,
              scale: tier.isNext && animateNextTier ? 1.02 : 1,
              boxShadow: tier.isNext && animateNextTier
                ? [
                  '0 4px 6px rgba(0, 0, 0, 0.1)',
                  '0 10px 20px rgba(56, 189, 248, 0.3)',
                  '0 4px 6px rgba(0, 0, 0, 0.1)'
                ]
                : '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            transition={{
              delay: Math.min(tier.members * 0.1, 1), // Cap the delay to prevent very long animations
              duration: 0.5,
              scale: {
                duration: 1.2,
                repeat: tier.isNext ? 1 : 0,
                repeatType: "reverse",
                ease: "easeInOut"
              }
            }}
            whileHover={{
              scale: 1.02,
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)'
            }}
            onHoverStart={() => setActiveHover(tier.members)}
            onHoverEnd={() => setActiveHover(null)}
            onMouseMove={tier.isNext ? handleMouseMove : undefined}
            onMouseLeave={tier.isNext ? handleMouseLeave : undefined}
            style={tier.isNext && !isMobile ? {
              transformStyle: 'preserve-3d',
              transform: `perspective(1000px) rotateX(${rotationValues.x}deg) rotateY(${rotationValues.y}deg)`
            } : {}}
          >
            {/* Tier circle with number */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                component={motion.div}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: isMobile ? 48 : 64,
                  height: isMobile ? 48 : 64,
                  borderRadius: '50%',
                  mr: isMobile ? 1.5 : 2,
                  bgcolor: tier.isCurrent
                    ? (darkMode ? 'rgba(99, 102, 241, 0.2)' : 'rgba(238, 242, 255, 1)')
                    : tier.isNext
                      ? (darkMode ? 'rgba(56, 189, 248, 0.2)' : 'rgba(186, 230, 253, 0.5)')
                      : (darkMode ? 'rgba(107, 114, 128, 0.2)' : 'rgba(243, 244, 246, 1)'),
                  border: '2px solid',
                  borderColor: tier.isCurrent
                    ? (darkMode ? 'rgba(99, 102, 241, 0.3)' : 'rgba(99, 102, 241, 0.2)')
                    : tier.isNext
                      ? (darkMode ? 'rgba(56, 189, 248, 0.3)' : 'rgba(56, 189, 248, 0.2)')
                      : (darkMode ? 'rgba(107, 114, 128, 0.3)' : 'rgba(229, 231, 235, 0.8)')
                }}
                animate={tier.isNext && animateNextTier ? {
                  background: [
                    'rgba(186, 230, 253, 0.5)',
                    'rgba(125, 211, 252, 0.6)',
                    'rgba(186, 230, 253, 0.5)'
                  ]
                } : {}}
                transition={{ duration: 2.5, ease: "easeInOut" }}
              >
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  className="tier-number"
                  sx={{
                    fontWeight: 700,
                    fontSize: isMobile ? '1.25rem' : '24px',
                    lineHeight: isMobile ? '48px' : '64px',
                    textAlign: 'center',
                    marginTop: isMobile ? '15px' : '22px',
                    color: tier.isCurrent
                      ? (darkMode ? 'var(--primary-light)' : 'rgb(79, 70, 229)')
                      : tier.isNext
                        ? (darkMode ? 'var(--secondary-light)' : 'rgb(2, 132, 199)')
                        : (darkMode ? 'var(--text-primary)' : 'rgb(75, 85, 99)')
                  }}
                >
                  {tier.members}
                </Typography>
              </Box>

              <Box sx={{ mr: isMobile ? 1 : 2 }}>
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  className="tier-label"
                  sx={{
                    fontWeight: 500,
                    color: 'var(--text-primary)',
                    fontSize: isMobile ? '0.95rem' : 'inherit'
                  }}
                >
                  {tier.label}
                </Typography>

                {/* Show savings badge for all except solo plan */}
                {tier.savings > 0 && (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mt: 0.5,
                    color: tier.isCurrent
                      ? (darkMode ? 'var(--primary-light)' : 'rgb(79, 70, 229)')
                      : tier.isNext
                        ? (darkMode ? 'var(--secondary-light)' : 'rgb(2, 132, 199)')
                        : (darkMode ? 'var(--text-primary)' : 'rgb(75, 85, 99)')
                  }}>
                    <DollarSign size={isMobile ? 12 : 14} />
                    <Typography
                      variant="body2"
                      className="tier-savings"
                      sx={{
                        ml: 0.5,
                        fontSize: isMobile ? '0.75rem' : 'inherit'
                      }}
                    >
                      {formatSavings(tier.savings)} savings
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Price */}
            <Box sx={{ textAlign: 'right' }}>
              <Box
                component={motion.div}
                className="tier-price"
                sx={{
                  fontSize: isMobile ? '1.5rem' : '1.875rem',
                  fontWeight: 700,
                  color: tier.isCurrent
                    ? (darkMode ? 'var(--primary-light)' : 'rgb(79, 70, 229)')
                    : tier.isNext
                      ? (darkMode ? 'var(--secondary-light)' : 'rgb(2, 132, 199)')
                      : (darkMode ? 'var(--text-primary)' : 'rgb(31, 41, 55)')
                }}
                animate={tier.isNext && animateNextTier ? {
                  scale: [1, 1.05, 1],
                  color: darkMode
                    ? ['var(--primary)', 'var(--secondary)', 'var(--primary)']
                    : ['rgb(37, 99, 235)', 'rgb(2, 132, 199)', 'rgb(37, 99, 235)']
                } : {}}
                transition={{ duration: 2.5, ease: "easeInOut" }}
              >
                {formatCurrency(tier.price)}
              </Box>
            </Box>

            {/* Status indicator */}
            {(tier.isCurrent || tier.isNext) && (
              <Box sx={{
                position: 'absolute',
                top: isMobile ? -7 : -8,
                right: isMobile ? 12 : 16,
                py: isMobile ? 0.25 : 0.5,
                px: isMobile ? 1 : 1.5,
                borderRadius: 12,
                fontSize: isMobile ? 10 : 12,
                fontWeight: 600,
                color: 'white',
                bgcolor: tier.isCurrent
                  ? (darkMode ? 'var(--primary)' : 'rgb(79, 70, 229)')
                  : (darkMode ? 'var(--secondary)' : 'rgb(14, 165, 233)')
              }}>
                {tier.isCurrent ? 'Current' : 'Next'}
              </Box>
            )}

            {/* Special effects for Next Tier - only on desktop and tablets to keep mobile performance fast */}
            {tier.isNext && (
              <>
                {/* Glow effect */}
                <Box
                  component={motion.div}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: isMobile ? 12 : 16,
                    opacity: 0,
                    pointerEvents: 'none'
                  }}
                  animate={{
                    opacity: animateNextTier ? [0, 0.3, 0] : 0,
                    boxShadow: animateNextTier
                      ? [
                        '0 0 0 rgba(56, 189, 248, 0)',
                        '0 0 20px rgba(56, 189, 248, 0.5)',
                        '0 0 0 rgba(56, 189, 248, 0)'
                      ]
                      : '0 0 0 rgba(56, 189, 248, 0)'
                  }}
                  transition={{ duration: 3, ease: "easeInOut" }}
                />

                {/* Particle dots - limited on mobile */}
                <Box
                  component={motion.div}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    overflow: 'hidden',
                    borderRadius: isMobile ? '12px' : '16px'
                  }}
                  animate={{ opacity: [0.4, 0.8, 0.4] }}
                  transition={{ repeat: Infinity, duration: 3 }}
                >
                  {/* Generate fewer particles on mobile for better performance */}
                  {Array.from({ length: isMobile ? 4 : 8 }).map((_, i) => (
                    <Box
                      key={`fixed-particle-${i}`}
                      sx={{
                        position: 'absolute',
                        width: isMobile ? '4px' : '5px',
                        height: isMobile ? '4px' : '5px',
                        borderRadius: '50%',
                        background: darkMode ? 'var(--secondary-light)' : '#0284c7',
                        opacity: 0.6,
                        top: `${10 + (i % (isMobile ? 2 : 4)) * 25}%`,
                        left: `${15 + Math.floor(i / (isMobile ? 2 : 4)) * 50}%`,
                        animation: `floatParticle ${4 + i % 4}s linear infinite ${i * 0.3}s`
                      }}
                    />
                  ))}
                </Box>

                {/* Savings Badge */}
                {savingsPerMember > 0 && (
                  <Box
                    component={motion.div}
                    sx={{
                      position: 'absolute',
                      bottom: isMobile ? -10 : -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      py: isMobile ? 0.3 : 0.5,
                      px: isMobile ? 1.5 : 2,
                      background: darkMode
                        ? 'linear-gradient(90deg, var(--primary), var(--secondary))'
                        : 'linear-gradient(90deg, rgb(2, 132, 199), rgb(8, 145, 178))',
                      color: 'white',
                      borderRadius: 24,
                      boxShadow: '0 4px 12px rgba(56, 189, 248, 0.3)',
                      fontSize: isMobile ? 12 : 14,
                      fontWeight: 600
                    }}
                    initial={{ y: 0 }}
                    animate={{
                      y: [0, -3, 0],
                      boxShadow: [
                        '0 4px 12px rgba(56, 189, 248, 0.3)',
                        '0 8px 20px rgba(56, 189, 248, 0.5)',
                        '0 4px 12px rgba(56, 189, 248, 0.3)'
                      ]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      repeatType: "mirror",
                      ease: "easeInOut"
                    }}
                  >
                    Save ${savingsPerMember.toFixed(2)}/mo
                  </Box>
                )}

                {/* Floating sparkles - limited on mobile */}
                {!isMobile && (
                  <>
                    <Box
                      component={motion.div}
                      sx={{
                        position: 'absolute',
                        right: 40,
                        top: '25%',
                        color: darkMode ? 'var(--primary-light)' : 'rgb(56, 189, 248)',
                      }}
                      animate={{
                        y: [0, -5, 0],
                        opacity: [0.5, 0.8, 0.5],
                        rotate: [0, 5, 0]
                      }}
                      transition={{
                        duration: 4,
                        repeat: Infinity,
                        repeatType: "mirror",
                        ease: "easeInOut"
                      }}
                    >
                      <Sparkles size={16} />
                    </Box>

                    <Box
                      component={motion.div}
                      sx={{
                        position: 'absolute',
                        right: 10,
                        bottom: '33%',
                        color: darkMode ? 'var(--secondary-light)' : 'rgb(8, 145, 178)',
                      }}
                      animate={{
                        y: [0, 5, 0],
                        opacity: [0.5, 0.8, 0.5],
                        rotate: [0, -5, 0]
                      }}
                      transition={{
                        duration: 5,
                        repeat: Infinity,
                        repeatType: "mirror",
                        delay: 0.5,
                        ease: "easeInOut"
                      }}
                    >
                      <Sparkles size={12} />
                    </Box>
                  </>
                )}

                {/* Mobile-only sparkle (single, simpler animation) */}
                {isMobile && (
                  <Box sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    color: darkMode ? '#7dd3fc' : '#0ea5e9',
                    opacity: 0.8
                  }}>
                    <Sparkles size={14} />
                  </Box>
                )}
              </>
            )}
          </Box>
        ))}
      </Box>

      {/* Bottom shadow indicator when scrollable */}
      {showBottomShadow && (
        <Box
          sx={{
            position: 'absolute',
            bottom: isMobile ? 155 : 195, // Above the share button section
            left: 0,
            right: 0,
            height: 8,
            background: 'linear-gradient(to top, rgba(0,0,0,0.05), transparent)',
            zIndex: 2,
            pointerEvents: 'none'
          }}
        />
      )}

      {/* Scroll down button */}
      {showBottomShadow && (
        <Box
          sx={{
            position: 'absolute',
            bottom: isMobile ? 155 : 195,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 3
          }}
        >
          <motion.div
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <Box
              onClick={scrollDown}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: isMobile ? 24 : 26,
                height: isMobile ? 24 : 26,
                borderRadius: '50%',
                bgcolor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                cursor: 'pointer',
                border: '1px solid',
                borderColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.05)'
              }}
            >
              <ChevronDown size={isMobile ? 14 : 16} color={darkMode ? 'rgba(255, 255, 255, 0.8)' : '#6B7280'} />
            </Box>
          </motion.div>
        </Box>
      )}

      {/* Share button section with metrics */}
      <Box sx={{ mt: isMobile ? 2 : 3 }}>
        {/* Share button - Using ModernShareButton for both mobile and desktop */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          {tierData && (
            <ModernShareButton
              circle={{
                id: tierData.circleId || 'circle',
                name: tierData.circleName || 'Circle',
                carrier: tierData.carrier || 'mobile',
                monthlyPayment: currentTier?.price || 0,
                available_slots: tierData.availableSlots || 0,
                features: tierData.features || []
              }}
              darkMode={darkMode}
              buttonText="Share Circle & Unlock Savings"
              size={isMobile ? "medium" : "large"}
              fullWidth={true}
              onShare={(circleData) => handleShare(tierData.circleId, circleData)}
            />
          )}
        </Box>

        {/* Share Metrics Card */}
        <Box
          component={motion.div}
          className="metrics-container"
          sx={{
            mt: isMobile ? 1.5 : 2,
            p: isMobile ? 1.5 : 2,
            borderRadius: isMobile ? 1.5 : 2,
            bgcolor: 'var(--metrics-bg)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
            boxShadow: darkMode ? '0 4px 12px rgba(0, 0, 0, 0.2)' : '0 4px 12px rgba(0, 0, 0, 0.05)'
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? 2 : 3,
                    color: 'var(--text-primary)'
                  }}
                >
                  {/* Views metric */}
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                    title="Your share link is getting attention!"
                  >
                    <Box
                      className="metric-icon views"
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'var(--icon-bg)',
                        color: darkMode ? 'var(--primary-light)' : 'var(--primary)',
                        p: isMobile ? 0.75 : 1,
                        borderRadius: '50%',
                        width: isMobile ? 28 : 32,
                        height: isMobile ? 28 : 32
                      }}
                    >
                      <Eye size={isMobile ? 14 : 16} />
                      {shareMetrics.recentActivity && (
                        <Box
                          component={motion.div}
                          className="activity-indicator"
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0 }}
                          transition={{ duration: 0.3 }}
                          sx={{
                            position: 'absolute',
                            top: -2,
                            right: -2,
                            width: isMobile ? 6 : 8,
                            height: isMobile ? 6 : 8,
                            borderRadius: '50%',
                            bgcolor: 'var(--success)'
                          }}
                        />
                      )}
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        className="metric-value"
                        sx={{
                          fontWeight: 600,
                          fontSize: isMobile ? '0.95rem' : '1rem',
                          lineHeight: 1,
                          color: 'var(--text-primary)'
                        }}
                      >
                        {shareMetrics.views}
                      </Typography>
                      <Typography
                        variant="caption"
                        className="metric-label"
                        sx={{
                          opacity: 0.8,
                          display: 'block',
                          fontSize: isMobile ? '0.7rem' : '0.75rem',
                          color: 'var(--text-secondary)'
                        }}
                      >
                        Views
                      </Typography>
                    </Box>
                  </Box>

                  {/* Clicks metric */}
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                    title="People are clicking your share link"
                  >
                    <Box
                      className="metric-icon clicks"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: darkMode ? 'rgba(14, 165, 233, 0.2)' : 'rgba(14, 165, 233, 0.1)',
                        color: darkMode ? 'var(--secondary-light)' : 'var(--secondary)',
                        p: isMobile ? 0.75 : 1,
                        borderRadius: '50%',
                        width: isMobile ? 28 : 32,
                        height: isMobile ? 28 : 32
                      }}
                    >
                      <MousePointer size={isMobile ? 14 : 16} />
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        className="metric-value"
                        sx={{
                          fontWeight: 600,
                          fontSize: isMobile ? '0.95rem' : '1rem',
                          lineHeight: 1,
                          color: 'var(--text-primary)'
                        }}
                      >
                        {shareMetrics.clicks}
                      </Typography>
                      <Typography
                        variant="caption"
                        className="metric-label"
                        sx={{
                          opacity: 0.8,
                          display: 'block',
                          fontSize: isMobile ? '0.7rem' : '0.75rem',
                          color: 'var(--text-secondary)'
                        }}
                      >
                        Clicks
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            </Box>

            {/* Status badge */}
            <Box
              className="active-status"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                bgcolor: darkMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.1)',
                color: 'var(--success)',
                py: isMobile ? 0.4 : 0.5,
                px: isMobile ? 0.8 : 1,
                borderRadius: isMobile ? 0.8 : 1,
                height: isMobile ? 24 : 28
              }}
              title="Your link is performing well"
            >
              <TrendingUp size={isMobile ? 12 : 14} />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: 'inherit',
                  fontSize: isMobile ? '0.7rem' : '0.75rem'
                }}
              >
                Active
              </Typography>
            </Box>
          </Box>

          {/* Motivational message */}
          <Box sx={{ mt: isMobile ? 1 : 1.5 }}>
            <Typography
              variant="body2"
              sx={{
                color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                fontStyle: 'italic',
                fontSize: isMobile ? '0.8rem' : '0.875rem'
              }}
            >
              People are viewing your invite link! {isMaxTier ? `You've reached the maximum tier!` : `Keep sharing to unlock the next tier.`}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Add the floatParticle keyframe animation */}
      <style jsx global>{`
        @keyframes floatParticle {
          0% { transform: translate(0, 0); }
          25% { transform: translate(10px, 10px); }
          50% { transform: translate(0, 20px); }
          75% { transform: translate(-10px, 10px); }
          100% { transform: translate(0, 0); }
        }
      `}</style>
    </Box>
  );
};

export default NextGenSavingsLadder;